import { useTranslation } from 'react-i18next';
import { AlertTriangle, Star } from 'lucide-react';
import { cn } from 'lib/utils';
import { Checkbox } from 'components/ui/checkbox';
import { technicianFullName } from 'pages/AdminTechnicians/csv';
import { initials } from 'components/rating/RatingForm';
import type { InterventionCandidate, StorePointStatus } from 'api/interventionRequestsApi';
import { EMPTY_MONEY_DRAFT, parseMoneyDraft, type MoneyDraft } from 'utils/money';
import { SKILL_MORE, SKILL_TAG } from './helpBlock';
import SecondaryZonePill from './SecondaryZonePill';
import TariffField from './TariffField';

/**
 * One proposed technician, ticked or not — several are solicited in one go since L5b (D45), so
 * the card carries a checkbox and no send button of its own: the panel's footer sends them all.
 *
 * **The rating is shown and does not enter the score**, as the mock-up has it: making it count
 * would be a product decision nobody has taken.
 *
 * **Distance shows only when the store is located** — otherwise the panel's own notice covers it once.
 *
 * A technician already solicited stays at his own rank, **disabled and marked** rather than
 * hidden or moved to the top: the reader sees why he cannot be ticked again.
 */

/** `distanceKm` rounded: whole from 10 km up, one decimal below, and a bare `0 km` if it rounds to zero. */
const formatDistanceKm = (km: number, locale: string): string => {
  const rounded1 = Math.round(km * 10) / 10;
  const decimals = rounded1 >= 10 || rounded1 === 0 ? 0 : 1;
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: 'kilometer',
    unitDisplay: 'short',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(km);
};

interface CandidateCardProps {
  candidate: InterventionCandidate;
  /** The mission's skills, so an uncovered one can be marked rather than merely absent. */
  missionSpecialtyIds: number[];
  /** The best candidate nobody has tried yet — the design's `isBest`. */
  recommended: boolean;
  /** Already solicited on this mission: the send would be refused, so the row is not tickable. */
  alreadySolicited: boolean;
  selected: boolean;
  storePoint?: StorePointStatus;
  onToggle: (candidate: InterventionCandidate) => void;
  /** A travel fee as typed on a ticked candidate (F19), parsed only at send time. */
  fee?: MoneyDraft;
  onFeeChange?: (fee: MoneyDraft) => void;
}

/** Six tags then a count, the mission's own skills first so a match reads before the rest. */
const VISIBLE_SKILLS = 6;

const CandidateCard = ({
  candidate,
  missionSpecialtyIds,
  recommended,
  alreadySolicited,
  selected,
  storePoint = 'missing',
  onToggle,
  fee = EMPTY_MONEY_DRAFT,
  onFeeChange,
}: CandidateCardProps) => {
  const { t, i18n } = useTranslation();
  const uncovered = new Set(candidate.uncoveredSpecialtyIds);
  const name = technicianFullName(candidate) || candidate.username;
  const boxId = `assign-candidate-${candidate.id}`;
  const required = new Set(missionSpecialtyIds);
  const skills = [...candidate.specialties].sort(
    (a, b) => Number(required.has(b.id)) - Number(required.has(a.id))
  );
  const hiddenSkills = skills.slice(VISIBLE_SKILLS);
  const distanceLabel =
    storePoint !== 'located'
      ? null
      : candidate.distanceKm === null
        ? t('Unknown distance')
        : formatDistanceKm(candidate.distanceKm, i18n.language);

  return (
    <li
      className={cn(
        // `relative` holds the checkbox's hidden form input inside the scrolled list.
        'relative rounded-xl border p-3',
        selected && 'border-ring ring-1 ring-ring',
        alreadySolicited && 'bg-muted/50 opacity-60'
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        <Checkbox
          id={boxId}
          className="mt-1 h-[18px] w-[18px] shrink-0"
          checked={selected}
          disabled={alreadySolicited}
          aria-label={name}
          onCheckedChange={() => onToggle(candidate)}
        />
        {/* The design's avatar: initials, reusing the helper the rating sheet already owns. */}
        <span
          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-zinc-100 text-[12px] font-semibold text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
          aria-hidden="true"
        >
          {initials(name)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <label
              htmlFor={boxId}
              className={cn(
                'truncate text-[13.5px] font-medium text-zinc-900 dark:text-zinc-50',
                !alreadySolicited && 'cursor-pointer'
              )}
            >
              {name}
            </label>
            {/* The directory's own marker, same triangle and same sr-only label. A **warning, not
                a block**: he is still tickable and still sendable — he is solicited by e-mail and
                can accept. Only the Jira participant write is at stake, so the wording here is the
                drawer's own and not the listing's « cannot be sent ». */}
            {!candidate.externalId && (
              <span
                className="inline-flex shrink-0 items-center text-amber-600 dark:text-amber-400"
                title={t('No Jira account linked: if he accepts, he cannot be added to the ticket participants. The solicitation can still be sent.')}
              >
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                <span className="sr-only">{t('Mobile account not provisioned')}</span>
              </span>
            )}
            {/* `isBest` in the design: the one the ranking puts first among those never asked. */}
            {recommended && (
              <span className="shrink-0 rounded-[5px] bg-emerald-50 px-1.5 py-0.5 text-[10.5px] font-semibold text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
                {t('Recommended')}
              </span>
            )}
            {/* Why the box is dead, on the row itself — a disabled control alone is a defect. */}
            {alreadySolicited && (
              <span className="ml-auto shrink-0 text-[11.5px] text-muted-foreground">
                {t('Already solicited')}
              </span>
            )}
          </div>
          {/*
            One line, star first, as the mock-up's card draws it — not the directory's stacked
            « 4,6 / 5 » over « N avis ». `RatingCell` keeps that shape for the directory, whose
            own mock-up has no star in the list cell; this card is the only place one is drawn.
            Still never a 0: an unrated technician says so in words.
          */}
          <p className="mt-1 flex items-center gap-1.5 truncate text-[12px] text-muted-foreground">
            {candidate.ratings.avg == null || candidate.ratings.count === 0 ? (
              <span className="text-muted-foreground/70">{t('Not rated yet')}</span>
            ) : (
              <>
                <Star className="h-3 w-3 shrink-0 fill-amber-500 text-amber-500" aria-hidden="true" />
                <span>
                  {candidate.ratings.avg.toLocaleString(i18n.language, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
                </span>
                <span className="text-muted-foreground/70">
                  {t('{{count}} rating', { count: candidate.ratings.count })}
                </span>
              </>
            )}
            {candidate.profile.company && <span className="truncate">· {candidate.profile.company}</span>}
          </p>

          {(distanceLabel || candidate.zone === 'secondary') && (
            <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
              {distanceLabel && <span>{distanceLabel}</span>}
              {candidate.zone === 'secondary' && (
                <>
                  <SecondaryZonePill />
                  <span>{t('possible fees')}</span>
                </>
              )}
            </p>
          )}

          {/* What he has already taken that day (D69) — a fact, not a warning: nothing here ranks
              or blocks. From 1 only: `0` and `null` both say nothing, for two different reasons. */}
          {!!candidate.sameDayMissions && (
            <p className="mt-1 text-[12px] text-muted-foreground">
              {t('{{count}} mission already accepted that day', { count: candidate.sameDayMissions })}
            </p>
          )}

          {/* No availability pill (D62): this list holds only technicians free on the mission's day,
              so a badge saying so on every row distinguishes nothing. It stays in the search
              panel, which deliberately filters neither perimeter nor availability (D20). */}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {candidate.specialties.length === 0 ? (
              <span className="text-[12px] text-muted-foreground/70">{t('No skill declared')}</span>
            ) : (
              <>
                {skills.slice(0, VISIBLE_SKILLS).map((specialty) => (
                  <span key={specialty.id} className={SKILL_TAG}>
                    {specialty.name}
                  </span>
                ))}
                {hiddenSkills.length > 0 && (
                  <span className={SKILL_MORE} title={hiddenSkills.map((specialty) => specialty.name).join(', ')}>
                    +{hiddenSkills.length}
                  </span>
                )}
              </>
            )}
          </div>

          {/*
            What the mission asks for and this technician does not carry. Named rather than merely
            absent from the list above: `uncovered` is what the score is built on, so the reader can
            see why one card sits below another instead of taking the order on faith.
          */}
          {missionSpecialtyIds.some((id) => uncovered.has(id)) && (
            <p className="mt-2 text-[12px] text-amber-700 dark:text-amber-400">
              {t('{{count}} required skill not covered', { count: candidate.uncoveredSpecialtyIds.length })}
            </p>
          )}

          {selected && onFeeChange && candidate.zone === 'secondary' && (
            <div className="mt-3">
              <TariffField
                id={`assign-fee-${candidate.id}`}
                label={t('Travel fee · optional')}
                hint={t('Let them know the amount by phone.')}
                error={t('Enter an amount, e.g. 40 or 40.50.')}
                text={fee.text}
                taxBasis={fee.taxBasis}
                refused={parseMoneyDraft(fee.text) === undefined}
                onTextChange={(text) => onFeeChange({ ...fee, text })}
                onTaxBasisChange={(taxBasis) => onFeeChange({ ...fee, taxBasis })}
              />
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

export default CandidateCard;
